<template>
  <div class="institution-import">
    <z-button
      class="institution-import__button"
      v-permissions:student.importation
      @click="open = true"
    >
      {{ title }}
    </z-button>

    <z-modal
      :open="open"
      :closable="false"
      size="auto"
      @closed="open = false"
    >
      <div v-if="!imported" class="institution-import__modal">
        <div class="institution-import__modal__wrapper">
          <div class="institution-import__modal__header">
            <div class="institution-import__modal__header__title">
              <z-title color="minor">
                {{ title }}
              </z-title>

              <z-title size="medium">
                {{ stepTitle }}
              </z-title>
            </div>

            <div class="institution-import__modal__header__current-step">
              <strong>Passo {{ step }}</strong> de 4
            </div>
          </div>

          <div class="institution-import__modal__content">
            <z-card v-if="step === 1">
              <div class="institution-import__modal__content__step-1">
                <div class="institution-import__modal__content__step-1__header">
                  <z-icon icon="university" size="large" />

                  <z-text size="large" weight="bold">
                    Selecione a instituição de ensino para iniciar a importação
                  </z-text>
                </div>

                <z-input-field class="select-institution-field__wrapper" label="Instituição de ensino">
                  <z-autocomplete
                    :icon="autocompleteInstitutionIcon"
                    :internal-search="false"
                    :options="institutionData"
                    :show-no-result="autocompleteInstitutionShowNoResult"
                    :value="institutionInput"
                    :clear-button-aria-label="autocompleteInstitutionHasArialLabel"
                    @clear="clear"
                    @input="handleInstitutionAutocompleteInput"
                    @select="handleInstitutionAutocompleteSelect"
                    ref="autocompleteInstitution"
                    label-by="name"
                    placeholder="Insira o ID, sigla ou nome da instituição"
                    class="select-institution-field__autocomplete"
                  >
                    <template v-slot:item="slotProps">
                      <div
                        :class="{
                          'select-institution-field__autocomplete-item': true,
                          'select-institution-field__autocomplete-item--small-vertical-padding': true,
                          'select-institution-field__autocomplete-item--is-highlighted': slotProps.isHighlighted
                        }"
                      >
                        <z-text
                          class="z-ovr-text--fw-medium select-institution-field__autocomplete-item-text"
                          :color="slotProps.isHighlighted && 'inverted' || 'interactive'"
                        >
                          {{ showInstitutionName(slotProps.item) }}
                        </z-text>

                        <z-text
                          class="select-institution-field__autocomplete-item-text"
                          size="small"
                          :color="slotProps.isHighlighted && 'inverted' || 'minor'"
                        >
                          {{ showQPId(slotProps.item) }}  |  {{ showQBId(slotProps.item) }}
                        </z-text>
                      </div>
                    </template>

                    <template v-slot:noResult>
                      <div class="select-institution-field__autocomplete-item">
                        <z-text
                          class="z-ovr-text--fw-medium select-institution-field__autocomplete-item-text"
                          color="major"
                        >
                          Nenhum resultado encontrado
                        </z-text>
                      </div>
                    </template>
                  </z-autocomplete>
                </z-input-field>
              </div>
            </z-card>

            <div v-else-if="step === 2" class="institution-import__modal__content__step-2">
              <z-card v-if="isValidating" class="institution-import__modal__content__step-2__loading">
                <div class="institution-import__modal__content__step-2__loading__message">
                  <z-loading variant="spinner" />

                  <z-text align="center">
                    Validando os dados da tabela e análisando possíveis conflitos...
                  </z-text>
                </div>
              </z-card>

              <div v-else class="institution-import__modal__content__step-2__content-wrapper">
                <z-alert
                  v-if="genericError"
                  class="institution-import__modal__content__step-2__error-message"
                  variant="danger"
                  title="Mensagem de falha"
                  :description="genericError"
                />

                <div class="institution-import__modal__content__step-2__content">
                  <z-card>
                    <div class="institution-import__modal__content__step-2__content__item">
                      <div class="institution-import__modal__content__step-2__content__item__header">
                        <div class="institution-import__modal__content__step-2__content__item__title">
                          <z-icon :icon="['far', 'file-alt']" size="large" />

                          <z-text size="large" weight="bold">
                            Tabela simplificada
                          </z-text>
                        </div>

                        <z-text>
                          Dados do aluno e valores globais por contratos.
                        </z-text>
                      </div>

                      <div class="institution-import__modal__content__step-2__content__item__content">
                        <z-button v-if="hasCompleteSpreadsheet" disabled>
                          Selecionar arquivo
                        </z-button>

                        <label
                          v-else-if="!hasImportSpreadsheet"
                          for="simple-spreadsheet"
                          class="z-button z-button--secondary z-button--medium"
                        >
                          <span class="z-button__text">
                            Selecionar arquivo
                          </span>
                        </label>

                        <div v-else class="z-button z-button--secondary z-button--medium">
                          <div class="institution-import__modal__content__step-2__content__item__uploaded-file">
                            <z-text :title="spreadsheetImport.file.name">
                              {{ spreadsheetFileName }}
                            </z-text>

                            <div
                              class="z-caption z-caption--interactive z-caption--right"
                              @click="clearSimpleSpreadsheetInput"
                            >
                              remover
                            </div>
                          </div>
                        </div>

                        <input
                          id="simple-spreadsheet"
                          ref="simpleSpreadsheet"
                          type="file"
                          accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                          @change="uploadSimpleSpreadsheet"
                        >

                        <z-text size="small" color="minor">
                          Tipo de arquivo aceito: XLSX
                        </z-text>

                        <z-link
                          size="small"
                          :href="simpleImportModelURL"
                          target="_blank"
                        >
                          Visualizar modelo de importação simplificada
                        </z-link>
                      </div>
                    </div>
                  </z-card>

                  <z-card>
                    <div class="institution-import__modal__content__step-2__content__item">
                      <div class="institution-import__modal__content__step-2__content__item__header">
                        <div class="institution-import__modal__content__step-2__content__item__title">
                          <z-icon :icon="['far', 'file-spreadsheet']" size="large" />

                          <z-text size="large" weight="bold">
                            Tabela completa
                          </z-text>
                        </div>

                        <z-text>
                          Dados do aluno e valores de mensalidades individuais por contratos.
                        </z-text>
                      </div>

                      <div class="institution-import__modal__content__step-2__content__item__content">
                        <z-button v-if="hasSimpleSpreadsheet" disabled>
                          Selecionar arquivo
                        </z-button>

                        <label
                          v-else-if="!hasImportSpreadsheet"
                          for="complete-spreadsheet"
                          class="z-button z-button--secondary z-button--medium"
                        >
                          <span class="z-button__text">
                            Selecionar arquivo
                          </span>
                        </label>

                        <div v-else class="z-button z-button--secondary z-button--medium">
                          <div class="institution-import__modal__content__step-2__content__item__uploaded-file">
                            <z-text :title="spreadsheetImport.file.name">
                              {{ spreadsheetFileName }}
                            </z-text>

                            <div
                              class="z-caption z-caption--interactive z-caption--right"
                              @click="clearCompleteSpreadsheetInput"
                            >
                              remover
                            </div>
                          </div>
                        </div>

                        <input
                          id="complete-spreadsheet"
                          ref="completeSpreadsheet"
                          type="file"
                          accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                          @change="uploadCompleteSpreadsheet"
                        >

                        <z-text size="small" color="minor">
                          Tipo de arquivo aceito: XLSX
                        </z-text>

                        <z-link
                          size="small"
                          :href="completeImportModelURL"
                          target="_blank"
                        >
                          Visualizar modelo de importação completa
                        </z-link>
                      </div>
                    </div>
                  </z-card>
                </div>
              </div>
            </div>

            <div v-else-if="step === 3" class="institution-import__modal__content__step-3">
              <div class="institution-import__modal__content__step-3__header">
                <z-title>
                  Encontramos {{ spreadsheetErrors.length }}
                  {{ spreadsheetErrors.length > 1 ? 'conflitos nos dados de sua' : 'conflito na sua' }}
                  tabela.
                </z-title>

                <z-text>
                  Corrija as informações abaixo e importe a tabela novamente:
                </z-text>
              </div>

              <z-card class="institution-import__modal__content__step-3__content">
                <div class="institution-import__modal__content__step-3__content__result">
                  <div
                    v-for="(error, index) in spreadsheetErrors"
                    :key="index"
                    class="institution-import__modal__content__step-3__content__result__item"
                  >
                    <z-text size="small" weight="bold">
                      COLUNA {{ error.column }} / LINHA {{ error.row }}
                    </z-text>

                    <z-text size="small">
                      <z-badge-text :value="error.value" variant="danger" />
                      {{ error.message }}
                    </z-text>

                    <z-divider v-if="!(index === (spreadsheetErrors.length - 1))" />
                  </div>
                </div>
              </z-card>
            </div>

            <div v-else-if="step === 4" class="institution-import__modal__content__step-4">
              <z-card v-if="isImporting" class="institution-import__modal__content__step-4__loading">
                <div class="institution-import__modal__content__step-4__loading__message">
                  <z-loading variant="spinner" />

                  <z-text align="center">
                    Realizando importação...
                  </z-text>
                </div>
              </z-card>

              <div class="institution-import__modal__content__step-4__content-wrapper" v-else>
                <div class="institution-import__modal__content__step-4__header">
                  <z-text size="large">
                    Confira abaixo os itens que serão importados de sua planilha:
                  </z-text>
                </div>

                <z-card class="institution-import__modal__content__step-4__content">
                  <div class="institution-import__modal__content__step-4__content__result">
                    <div class="institution-import__modal__content__step-4__content__result__item">
                      <z-text size="small" weight="bold">
                        ALUNOS
                      </z-text>

                      <z-text v-if="statistics.students === 1" size="small" color="success">
                        {{ statistics.students }} novo aluno.
                      </z-text>

                      <z-text v-else size="small" color="success">
                        {{ statistics.students }} novos alunos.
                      </z-text>

                      <z-divider />
                    </div>

                    <div class="institution-import__modal__content__step-4__content__result__item">
                      <z-text size="small" weight="bold">
                        MENSALIDADES
                      </z-text>

                      <z-text
                        v-if="statistics.installments === 1"
                        size="small"
                        color="success"
                      >
                        {{ statistics.installments }} nova mensalidade no valor
                        de {{ statistics.maximum_bill_value }}.
                      </z-text>

                      <z-text
                        v-else-if="
                          statistics.minimum_bill_value ===
                            statistics.maximum_bill_value
                        "
                        size="small"
                        color="success"
                      >
                        {{ statistics.installments }} novas mensalidades com
                        valor de {{ statistics.maximum_bill_value }}.
                      </z-text>

                      <z-text
                        v-else
                        size="small"
                        color="success"
                      >
                        {{ statistics.installments }} novas mensalidades entre
                        {{ statistics.minimum_bill_value }} e
                        {{ statistics.maximum_bill_value }}.
                      </z-text>

                      <z-divider />
                    </div>

                    <div class="institution-import__modal__content__step-4__content__result__item">
                      <z-text size="small" weight="bold">
                        CURSOS
                      </z-text>

                      <z-text v-if="statistics.courses === 1" size="small" color="success">
                        {{ statistics.courses }} novo curso.
                      </z-text>

                      <z-text v-else size="small" color="success">
                        {{ statistics.courses }} novos cursos.
                      </z-text>

                      <z-divider />
                    </div>

                    <div class="institution-import__modal__content__step-4__content__result__item">
                      <z-text size="small" weight="bold">
                        UNIDADES DE ENSINO / CAMPUS
                      </z-text>

                      <z-text v-if="statistics.campuses === 1" size="small" color="success">
                        {{ statistics.campuses }} nova unidade de ensino.
                      </z-text>

                      <z-text v-else size="small" color="success">
                        {{ statistics.campuses }} novas unidades de ensino.
                      </z-text>
                    </div>
                  </div>
                </z-card>
              </div>
            </div>
          </div>
        </div>

        <div class="institution-import__modal__footer">
          <z-button
            variant="tertiary"
            :disabled="isValidating || isImporting"
            @click="close"
          >
            Cancelar
          </z-button>

          <div class="institution-import__modal__footer__actions">
            <z-button
              v-if="step > 1"
              variant="secondary"
              :disabled="isValidating || isImporting"
              @click="previousStep"
            >
              Voltar
            </z-button>

            <z-button
              v-if="step === 4"
              :disabled="!canGoToNextStep || isImporting"
              @click="confirmImport"
            >
              Confirmar e importar
            </z-button>

            <z-button
              v-else
              :disabled="isValidating || !canGoToNextStep"
              @click="nextStep"
            >
              Avançar
            </z-button>
          </div>
        </div>
      </div>

      <div v-else-if="imported" class="institution-import__modal--result">
        <z-icon color="success" size="giant" :icon="['far', 'check-circle']" />

        <z-title align="center" size="large">
          Importação realizada com sucesso!
        </z-title>

        <z-text align="center">
          Os dados importados já estão disponíveis na lista de alunos.
        </z-text>

        <z-button size="large" @click="close">
          Concluir
        </z-button>
      </div>

      <div v-else class="institution-import__modal--result">
        <z-icon color="danger" size="giant" :icon="['far', 'times-circle']" />

        <z-title align="center" size="large">
          Importação não realizada
        </z-title>

        <z-text align="center">
          {{ genericError }}
        </z-text>

        <z-button size="large" @click="close">
          Encerrar
        </z-button>
      </div>
    </z-modal>
  </div>
</template>

<script>
import { debounce } from 'lodash';

import InstitutionsService from 'services/InstitutionsService';
import Permissions from 'mixins/Permissions';

const baseURL = 'https://docs.google.com/spreadsheets';
const simpleImportModelURL = `${baseURL}/d/1dLRDTWcQmtHFn0MOgpHQyuSUz2rFPzFLbPHkQQtj82g/edit?usp=drivesdk`;
const completeImportModelURL = `${baseURL}/d/14ZzUuVlb6wFcz7-k_1My181lo8BzKblBe0HjZkQEGm0/edit?usp=drivesdk`;

export default {
  mixins: [Permissions],
  data() {
    return {
      title: 'Importação de alunos e mensalidades',
      open: false,
      step: 1,
      institutionInput: '',
      institutionSelected: null,
      institutionData: [],
      institutionAutocompleteHasFocus: false,
      spreadsheetImport: {
        id: null,
        type: null,
        file: null,
        url: null,
      },
      statistics: null,
      genericError: null,
      spreadsheetErrors: null,
      canImport: false,
      validating: false,
      importing: false,
      imported: false,
      simpleImportModelURL,
      completeImportModelURL,
    };
  },
  mounted() {
    this.$watch(
      '$refs.autocompleteInstitution.hasFocus',
      this.handleInstitutionAutocompleteFocus,
    );
  },
  computed: {
    stepTitle() {
      if (this.step === 1) return 'Instituição de ensino';
      if (this.step === 2) return 'Selecione a tabela de importação';
      if (this.step === 3) return 'Análise de conflitos';
      if (this.step === 4) return 'Resumo da importação';
      return '';
    },
    autocompleteInstitutionIcon() {
      if (this.hasSelectedInstitution) return ['fas', 'check-circle'];
      return '';
    },
    autocompleteInstitutionHasArialLabel() {
      return this.institutionAutocompleteHasFocus ? 'Limpar busca por instituição' : null;
    },
    autocompleteInstitutionShowNoResult() {
      return Boolean(this.institutionInput && this.institutionInput.length >= 1 && !this.institutionData.length);
    },
    hasSelectedInstitution() {
      return this.institutionSelected !== null;
    },
    hasImportSpreadsheet() {
      return this.spreadsheetImport.file && (this.hasSimpleSpreadsheet || this.hasCompleteSpreadsheet);
    },
    hasSimpleSpreadsheet() {
      return this.spreadsheetImport.type === 'simple';
    },
    hasCompleteSpreadsheet() {
      return this.spreadsheetImport.type === 'complete';
    },
    spreadsheetFileName() {
      if (this.spreadsheetImport && this.spreadsheetImport.file) {
        if (this.spreadsheetImport.file.name.length > 25) {
          return this.spreadsheetImport.file.name.slice(0, 25).trim().concat('...');
        }
        return this.spreadsheetImport.file.name;
      }
      return '';
    },
    isValidating() {
      return this.validating === true;
    },
    isImporting() {
      return this.importing === true;
    },
    canGoToNextStep() {
      if (this.step === 1) return this.hasSelectedInstitution;
      if (this.step === 2) return this.hasSelectedInstitution && this.hasImportSpreadsheet && !this.validating;
      if (this.step === 4) return this.canImport;
      return false;
    },
  },
  methods: {
    close() {
      this.open = false;
      this.step = 1;
      this.clear();
    },
    clear() {
      this.institutionInput = '';
      this.institutionSelected = null;
      this.institutionData = [];
      this.institutionAutocompleteHasFocus = false;
      this.statistics = null;
      this.validating = false;
      this.importing = false;
      this.imported = false;
      this.clearImportValidation();
      this.removeImportSpreadsheet();
    },
    clearImportValidation() {
      this.canImport = false;
      this.spreadsheetErrors = null;
      this.genericError = null;
    },
    removeImportSpreadsheet() {
      this.spreadsheetImport = { type: null, file: null };
    },
    clearSimpleSpreadsheetInput() {
      this.removeImportSpreadsheet();
      this.$refs.simpleSpreadsheet.value = '';
    },
    clearCompleteSpreadsheetInput() {
      this.removeImportSpreadsheet();
      this.$refs.completeSpreadsheet.value = '';
    },
    searchInstitution: debounce(function searchInstitution(reference) {
      InstitutionsService.searchInstitution(reference)
        .then(({ data }) => {
          this.institutionData = data;
        });
    }, 500),
    handleInstitutionAutocompleteInput(institutionInput) {
      this.institutionInput = institutionInput;

      if (institutionInput) {
        this.searchInstitution(institutionInput);
      }
    },
    handleInstitutionAutocompleteSelect(institutionSelected) {
      this.institutionSelected = institutionSelected;
    },
    handleInstitutionAutocompleteFocus(newValue) {
      this.institutionAutocompleteHasFocus = newValue;
    },
    showInstitutionName(institution) {
      if (institution.group_name !== null) return `${institution.name} (${institution.group_name})`;
      return institution.name;
    },
    showQPId(institution) {
      return `Código no Quero Pago: ${institution.id}`;
    },
    showQBId(institution) {
      return `Código no Quero Bolsa: ${institution.qb_university_id}`;
    },
    uploadSimpleSpreadsheet(event) {
      this.uploadFile(event, 'simple');
    },
    uploadCompleteSpreadsheet(event) {
      this.uploadFile(event, 'complete');
    },
    uploadFile({ target }, type) {
      this.clearImportValidation();

      const { files } = target;
      const [file] = files;

      if (files && file) {
        this.spreadsheetImport.type = type;
        this.spreadsheetImport.file = file;
      }
    },
    validateImport() {
      this.validating = true;

      const { id } = this.institutionSelected;
      const { file, type } = this.spreadsheetImport;

      InstitutionsService.validateImport(file, id, type)
        .then(({ data }) => {
          const {
            spreadsheet_id: spreadsheetId,
            spreadsheet_url: spreadsheetURL,
            continue_import: canImport,
            row_errors: spreadsheetErrors,
            generic_error: genericError,
            statistics,
          } = data;

          this.spreadsheetImport.id = spreadsheetId;
          this.spreadsheetImport.url = spreadsheetURL;
          this.canImport = canImport;
          this.spreadsheetErrors = spreadsheetErrors.length ? spreadsheetErrors : null;
          this.genericError = genericError;
          this.statistics = statistics;

          if (!this.genericError) {
            this.nextStep();
          }
        })
        .catch((error) => {
          const unexpectedErrorMessage = 'Ocorreu um erro inesperado na validação, contate o responsável.';
          const unansweredErrorMessage = 'Ocorreu uma falha na validação porém sem mensagem, contate o responsável.';

          this.handleError(error, unexpectedErrorMessage, unansweredErrorMessage);
        })
        .finally(() => {
          this.validating = false;
        });
    },
    confirmImport() {
      this.importing = true;

      const { id: institutionId } = this.institutionSelected;
      const { id: spreadsheetId, url, type } = this.spreadsheetImport;

      InstitutionsService.confirmImport(institutionId, spreadsheetId, url, type)
        .then(({ data }) => {
          const { status } = data;

          this.imported = status;
          this.nextStep();
        })
        .catch((error) => {
          const unexpectedErrorMessage = 'Ocorreu um erro inesperado na importação, contate o responsável.';
          const unansweredErrorMessage = 'Ocorreu uma falha na importação porém sem mensagem, contate o responsável.';

          this.handleError(error, unexpectedErrorMessage, unansweredErrorMessage);
        })
        .finally(() => {
          this.importing = false;
        });
    },
    handleError(error, unexpectedErrorMessage, unansweredErrorMessage) {
      if (error.status === 500) {
        this.genericError = unexpectedErrorMessage;
      } else if (error.response) {
        if (error.response.data.message) {
          this.genericError = error.response.data.message;
        } else {
          this.genericError = unansweredErrorMessage;
        }
      } else if (error.request) {
        this.genericError = unansweredErrorMessage;
      } else {
        this.genericError = unexpectedErrorMessage;
      }
    },
    nextStep() {
      if (this.step === 2 && !this.canImport && this.spreadsheetErrors === null && this.genericError === null) {
        this.validateImport();
      } else if (this.step === 2 && this.canImport) {
        this.step += 2;
      } else {
        this.step += 1;
      }
    },
    previousStep() {
      if (this.step === 3) {
        this.clearImportValidation();
        this.step -= 1;
      } else if (this.step === 4 && this.canImport) {
        this.clearImportValidation();
        this.step -= 2;
      } else {
        this.step -= 1;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.institution-import__modal {
  display: flex;
  flex-direction: column;
  min-width: 800px;

  &--result {
    text-align: center;
    margin-bottom: var(--space-extra-jumbo);
    margin-left: var(--space-extra-colossal);
    margin-top: var(--space-extra-jumbo);
    margin-right: var(--space-extra-colossal);

    & > .z-text {
      margin-top: var(--space-small);
    }

    & > .z-button {
      margin-top: var(--space-large);
    }
  }
}

.institution-import__modal__wrapper {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  margin-bottom: var(--space-jumbo);
}

.institution-import__modal__header {
  display: flex;
  justify-content: space-between;
  margin-bottom: var(--space-extra-large);
}

.institution-import__modal__header__title > .z-title:first-of-type {
  margin-bottom: var(--space-small);
}

.institution-import__modal__content {
  height: 100%;
}

.institution-import__modal__content__step-1__header {
  align-items: center;
  display: flex;
  margin-bottom: var(--space-large);

  & > .z-icon {
    margin-right: var(--space-medium);
  }

  & > .z-text {
    margin-bottom: 0;
  }
}

.select-institution-field__wrapper {
  margin: 0 !important;
}

.select-institution-field__autocomplete {
  --autocomplete-result-height: 140px;
}

.select-institution-field__autocomplete-item {
  padding: var(--space-medium);
  border-top: var(--size-nano) solid var(--color-gray-050);
}

.select-institution-field__autocomplete-item--small-vertical-padding {
  padding-bottom: var(--space-small);
  padding-top: var(--space-small);
}

.select-institution-field__autocomplete-item--is-highlighted {
  background-color: var(--color-font-interactive-normal);
}

.select-institution-field__autocomplete-item-text {
  margin-bottom: 0;
}

.institution-import__modal__content__step-2 {
  display: flex;
}

.institution-import__modal__content__step-2__loading {
  display: flex;
  justify-content: center;
  width: 100%;
}

.institution-import__modal__content__step-2__loading__message {
  padding-bottom: var(--space-extra-colossal);
  padding-top: var(--space-extra-colossal);
  margin: auto;
  width: 100%;
  text-align: center;

  & > .z-loading {
    margin-bottom: var(--space-medium);
  }
}

.institution-import__modal__content__step-2__error-message {
  margin-bottom: var(--space-large);
  max-width: 800px;
}

.institution-import__modal__content__step-2__content-wrapper {
  display: flex;
  flex-direction: column;
}

.institution-import__modal__content__step-2__content {
  display: flex;

  & > .z-card {
    align-items: stretch;
    display: flex;
    flex-direction: column;
    width: 388px;
  }

  & > .z-card:first-of-type {
    margin-right: var(--space-large);
  }
}

.institution-import__modal__content__step-2__content__item {
  flex-grow: 1;
  display: flex;
  justify-content: space-between;
  flex-direction: column;

  & > .z-button {
    margin-bottom: var(--space-small);
  }

  & > .z-icon {
    margin-right: var(--space-medium);
  }
}

.institution-import__modal__content__step-2__content__item__header {
  display: flex;
  flex-direction: column;
}

.institution-import__modal__content__step-2__content__item__title {
  align-items: baseline;
  display: flex;

  & > .z-icon {
    margin-right: var(--space-small);
  }
}

.institution-import__modal__content__step-2__content__item__content {
  display: flex;
  flex-direction: column;

  & > .z-button {
    margin-bottom: var(--space-small);
  }
}

.institution-import__modal__content__step-2__content__item__uploaded-file {
  align-items: baseline;
  display: flex;
  justify-content: space-between;

  & > .z-text {
    margin-right: var(--space-medium);
    margin-bottom: 0;
  }
}

input[type="file"] {
  display: none;
}

.institution-import__modal__content__step-3 {
  display: flex;
  flex-direction: column;
}

.institution-import__modal__content__step-3__header {
  margin-bottom: var(--space-medium);
}

.institution-import__modal__content__step-3__content {
  display: flex;
  justify-content: center;
  overflow-y: scroll;
  max-height: 438px;
}

.institution-import__modal__content__step-3__content__result {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.institution-import__modal__content__step-3__content__result__item {
  margin-top: var(--space-large);
  width: 100%;

  &:first-of-type {
    margin-top: 0;
  }

  &:last-of-type > .z-text:last-of-type {
    margin-bottom: var(--space-large);
  }

  & > .z-text:first-of-type {
    margin-bottom: 0;
  }

  & > .z-text:last-of-type {
    margin-bottom: var(--space-large);
  }
}

.institution-import__modal__content__step-4 {
  display: flex;
  flex-direction: column;
}

.institution-import__modal__content__step-4__loading {
  display: flex;
  justify-content: center;
  width: 100%;
}

.institution-import__modal__content__step-4__loading__message {
  padding-bottom: var(--space-extra-colossal);
  padding-top: var(--space-extra-colossal);
  margin: auto;
  width: 100%;
  text-align: center;

  & > .z-loading {
    margin-bottom: var(--space-medium);
  }
}

.institution-import__modal__content__step-4__error-message {
  margin-bottom: var(--space-large);
  max-width: 800px;
}

.institution-import__modal__content__step-4__content-wrapper {
  display: flex;
  flex-direction: column;
}

.institution-import__modal__content__step-4__header {
  margin-bottom: var(--space-medium);
}

.institution-import__modal__content__step-4__content {
  display: flex;
  justify-content: center;
}

.institution-import__modal__content__step-4__content__result {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.institution-import__modal__content__step-4__content__result__item {
  margin-top: var(--space-large);
  width: 100%;

  &:first-of-type {
    margin-top: 0;
  }

  &:last-of-type > .z-text:last-of-type {
    margin-bottom: 0;
  }

  & > .z-text:first-of-type {
    margin-bottom: var(--space-small);
  }

  & > .z-text:last-of-type {
    margin-bottom: var(--space-large);
  }
}

.institution-import__modal__footer {
  display: flex;
  justify-content: space-between;
}

.institution-import__modal__footer__actions > .z-button:nth-child(1) {
  margin-right: var(--space-medium);
}
</style>
